import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router";
import EmailMethodPage from "../auth/email-method";
import VerifyCodePage from "../auth/verify-code";
import {
  Card,
  CardContent,
  CardHeader,
} from "@/components/ui/card"
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert"
import { useCustomParam } from "@/hooks";
import OAuthButtons from "../../components/oauth-buttons";
import { Button } from "@/components/ui/button";
import EmailIcon from "@/components/svg/EmailIcon";
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldSeparator
} from "@/components/ui/field"
import AlertCircleIcon from "@/components/svg/alert-circle";
import { useVerifyReferrer, useStartUserOAuthSession } from "@/features/auth/mutations";
import RedirectView from "./redirect-view";
import AlreadyHasCodePage from "./already-has-code";

const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const getParam = useCustomParam();

  const rid = getParam("rid");
  const referrer = getParam("referrer");
  const username = getParam("username");
  const token = getParam("token");
  const ridOrRef = rid || referrer;

  // Get the current view from location state
  const view = (location.state as { view?: string })?.view;

  const verifyReferrerMutation = useVerifyReferrer();
  const startUserOAuthSession = useStartUserOAuthSession()

  const [redirectView, setRedirectView] = useState<boolean>(false);
  const [error, setError] = useState("");
  
  const handleNavigateToEmailMethod = () => {
    return navigate(location.pathname + location.search, { 
      state: { view: 'email' },
      replace: true 
    });
  };

  useEffect(() => {
    if (!ridOrRef) {
      navigate("/error");
      return;
    }

    const mutationPayload = referrer 
      ? { referrer, username } 
      : { rid: rid || undefined, username };

    verifyReferrerMutation.mutate(mutationPayload, {
      onError() {
        navigate("/error");
      },
    });

    if (token && ridOrRef) {
      setRedirectView(true)
      startUserOAuthSession.mutate({username: username || '', referrer: ridOrRef || '', token}, {
        onSuccess(data) {
          let redirectUrl = "";
          
          // Check if there is a query parameter before structuring the redirect
          if (ridOrRef.indexOf("?") != -1 || ridOrRef.indexOf("&") != -1) {
            redirectUrl = `${ridOrRef}&token=${data.token}&username=${data.username}`;
          } else {
            redirectUrl = `${ridOrRef}?token=${data.token}&username=${data.username}`;
          }

          window.location.replace(redirectUrl);
        },
        onError() {
          setError("There was a problem during authentication, please contact us at contact@getcollectr.com");
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rid, referrer, username, token]);

  // Delete all cookies to prevent oauth account selections from being saved
  useEffect(() => {
    const cookies = document.cookie.split(";");

    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
      document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
    }
  }, []);

  
  // Render different views based on state
  if (redirectView) {
    return <RedirectView />;
  }

  if (view === 'email') {
    return <EmailMethodPage />;
  }
  
  if (view === 'already-has-code') {
    return <AlreadyHasCodePage />;
  }

  if (view === 'verify-code') {
    return <VerifyCodePage />;
  }

  return (
    <>
      <div className='flex flex-col justify-center my-auto min-h-screen'>
        <Card className='sm:max-w-md w-full mx-auto sm:min-h-min min-h-screen sm:rounded-xl rounded-none'>        
          <CardHeader>
            <div className="flex justify-between">
              <div className="mx-auto">
                <img
                  className="dark:hidden h-12 text-center block"
                  src={"/images/black-logo.png"}
                  alt="Collectr"
                />
                <img
                  className="hidden h-12 text-center dark:block"
                  src={"/images/white-logo.png"}
                  alt="Collectr"
                />

              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="sm:mt-0 mt-4 flex flex-col justify-center">
              <FieldGroup>
                <Field>
                  {error && (
                    <Alert variant="destructive" className="[&>svg]:size-5">
                      <AlertCircleIcon />
                      <AlertTitle>There was a problem during authentication</AlertTitle>
                      <AlertDescription>
                        {error}
                      </AlertDescription>
                    </Alert>
                  )}

                  <FieldLabel htmlFor="recommended">
                    Recommended
                  </FieldLabel>
                  <Button
                    onClick={handleNavigateToEmailMethod}
                    className="w-full"
                    type="submit"
                    variant={'brandPrimary'}
                    size={'lg'}
                  >
                    <EmailIcon />
                    Continue with email
                  </Button>

                  <FieldDescription>
                    We <span className="font-bold italic">strongly </span> recommend
                    this option if you are on Android 12 or lower or have
                    experienced login issues.
                  </FieldDescription>
                </Field>
                
                <FieldSeparator>Or continue with</FieldSeparator>

                <OAuthButtons rid={ridOrRef} username={username} />

                <Alert variant="default" className="[&>svg]:size-5">
                  <AlertCircleIcon />   
                  <AlertTitle>Lost your account data?</AlertTitle>
                  <AlertDescription className="flex flex-row items-center">
                  <p>We can help! Please fill in</p>
                    <a
                      href="https://forms.gle/snMfnmq9gywiTM9c9"
                      className="text-sm underline underline-offset-2"
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      this form.
                    </a>
                  </AlertDescription>
                </Alert>
              </FieldGroup>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default Auth;
